import {
  DashboardState,
  IAction,
  IDocumentParameterDefinition,
  IDocumentParameterInput,
  ISymbol
} from "@kuika/kuika-cl-model";
import { FunctionComponent, KeyboardEvent, MouseEvent, useEffect, useState } from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import useLoadingPresentation, { LoadingPresentation } from "../../../shared/hooks/useLoadingPresentation";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import Trash12RedSvg from "../../../svgs/small-icons/trash-12-red.svg";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import { KuikaEditorHeader } from "../../ui-kit/kuika-editor-header/kuika-editor-header";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaEditorIdentity } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaTable, { KuikaTableColumn } from "../../ui-kit/kuika-table/kuika-table";
import KuikaSmallMediumText from "../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import { getEditorGlyph, IEditorGlyph } from "../data-diagram/editor-switcher/editor-glyph";
import EditorGlyphIcon from "../data-diagram/editor-switcher/editor-glyph-icon";
import { SymbolPicker } from "../right-panel/properties-panel/symbol-picker";
import { countBoundDocumentParameters, isDocumentParameterBound } from "./document-parameter-binding";
import "./document-parameters-section.scss";

const DOCUMENT_PARAMETERS_GLYPH: IEditorGlyph = getEditorGlyph({ kind: "documentParameters" });

/**
 * The parameter table for the Document Parameters tool (KOA-17254): each `<TAG>` and where its value comes from.
 *
 * <b>This surface answers exactly one question</b> — which tag gets which value — so it is a table and nothing else.
 * The things a value is built from are authored in the right panel: the declared document inputs, and the source
 * actions whose own parameters are wired there too. Three versions got this wrong in different ways: a select
 * listing every action definition in the project on every row, then a cascader inside this canvas, then the inputs
 * editor stacked above the table as a second form. Each made the canvas a place where unrelated concerns competed
 * for the same space.
 *
 * <b>Shape: the one Session Variables already uses</b> — a single compose strip above a list of what has been
 * composed. The earlier version made every saved row a live form: each one carried an editable field and an open
 * symbol picker, so a list of twenty parameters was twenty forms, and nothing distinguished the row being edited
 * from the nineteen that were merely being read. Here one row is edited at a time, in the strip, and the list below
 * is a list. It is the same two-part arrangement as Session Variables — the surface next to this one in the same
 * tree — so the two do not have to be learned separately.
 */

/**
 * Mirrors DocumentParameterTagSyntax on the backend: Unicode uppercase letters, digits and underscore, 2–64
 * chars, at least one letter. Kept in sync deliberately rather than shared, because the backend is the enforcing
 * authority — this copy only gives immediate feedback in the form.
 */
const TAG_NAME_PATTERN: RegExp = /^[\p{Lu}\p{Nd}_]{2,64}$/u;
const HAS_LETTER_PATTERN: RegExp = /\p{Lu}/u;

const isValidTagName = (name: string): boolean => TAG_NAME_PATTERN.test(name) && HAS_LETTER_PATTERN.test(name);

/**
 * What a picked value reads as in the list.
 *
 * Built from the symbol rather than from its `label`, because a leaf's label is the field or input name alone —
 * two rows fed by different actions' `Ad` column would then be indistinguishable. The action is named by the same
 * label the right panel's source-action list shows, so the two surfaces refer to it identically.
 */
const describeValue = (symbol: ISymbol | undefined, actions: IAction[]): string => {
  if (!symbol) return "";
  if (symbol.inputName) return symbol.inputName;

  if (symbol.actionDefinitionId && symbol.fieldName) {
    const source: IAction | undefined = actions.find(
      (action: IAction) => action.actionDefinitionId === symbol.actionDefinitionId
    );
    return `${source?.label ?? "Source action"}.${symbol.fieldName}`;
  }

  return symbol.label ?? "";
};

/** A row on screen: the saved parameters, plus the empty one at the end where the next is written. */
interface IDocumentParameterRow extends IDocumentParameterDefinition {
  isDraft?: boolean;
}

/**
 * A saved row's name, held while it is being typed and handed over when the field is left. The row is keyed by its
 * tag name, so writing every keystroke back would re-key the row and remount the input mid-word. A refused name
 * stays in the cell to be corrected.
 */
interface ITagNameCellProps {
  value: string;
  onCommit: (value: string) => boolean;
}

const TagNameCell = ({ value, onCommit }: ITagNameCellProps): JSX.Element => {
  const [text, setText] = useState<string>(value);

  useEffect((): void => setText(value), [value]);

  const commit = (): void => {
    const trimmed: string = text.trim();
    if (trimmed === value) return;
    if (!onCommit(trimmed)) return;
  };

  return (
    <KuikaInput
      borderless
      value={text}
      data-testid={`documentParameterTagName-${value}`}
      onChange={(_event: unknown, next: string | number): void => setText(String(next ?? ""))}
      onBlur={commit}
      onPressEnter={commit}
    />
  );
};

interface IDocumentParametersSectionProps {
  projectId: string;
}

const DocumentParametersSectionInner: FunctionComponent<IDocumentParametersSectionProps & IWithSDProps> = ({
  sd
}: IDocumentParametersSectionProps & IWithSDProps) => {
  const [definitions, setDefinitions] = useState<IDocumentParameterDefinition[]>([]);
  /**
   * Where SAVE stands. The section stays open after a save, so the button is the confirmation: it rolls to Saving,
   * then Saved (held long enough to read) or Try again. Owned by `SaveStatus`, never set directly.
   */
  const [saveStatus, setSaveStatus] = useState<KuikaButtonStatus>("idle");
  const [saveMachine] = useState<SaveStatus>((): SaveStatus => new SaveStatus(setSaveStatus));
  useEffect((): (() => void) => (): void => saveMachine.dispose(), [saveMachine]);
  /**
   * The list as last saved, for the dirty check.
   *
   * The right panel commits on every edit while this table waits for Save — two save models on one surface. The
   * table cannot autosave, because a half-typed tag name fails the same validation the backend applies, so instead
   * the difference is made visible: Save is inert until something actually changed.
   */
  const [savedSnapshot, setSavedSnapshot] = useState<string>("[]");
  /** The last row of the table. A parameter joins the list once it has both a name and a value. */
  const [draftTagName, setDraftTagName] = useState<string>("");
  const [draftSymbol, setDraftSymbol] = useState<ISymbol | undefined>(undefined);
  /**
   * Bumped whenever the draft value is replaced from outside the picker — cleared after an add, or loaded from the
   * row being edited. The picker holds its selection internally, so without a new key it keeps showing the previous
   * row's field.
   */
  const [pickerGeneration, setPickerGeneration] = useState<number>(0);
  /** Raised by the back control when the table is dirty; the answer is what actually leaves. */
  const [isClosePromptOpen, setIsClosePromptOpen] = useState<boolean>(false);

  // Both lists are authored in the right panel; the table only reads them to know what a value can be.
  const actions: IAction[] = sd?.documentParameterEvent?.actions ?? [];
  const inputs: IDocumentParameterInput[] = sd?.documentParameterInputs ?? [];
  const isLoading: boolean = sd?.isDocumentParametersLoading === true;
  const hasSources: boolean = actions.length > 0 || inputs.length > 0;
  // The list in outline while its first load has nothing to show; a reload keeps what is on screen.
  const presentation: LoadingPresentation = useLoadingPresentation({
    isLoading,
    hasData: definitions.length > 0 || hasSources
  });

  // The provider owns the loaded section; the form edits a local copy and hands it back on save.
  useEffect(() => {
    const loaded: IDocumentParameterDefinition[] = sd?.documentParameterDefinitions ?? [];
    setDefinitions(loaded);
    setSavedSnapshot(JSON.stringify(loaded));
  }, [sd?.documentParameterDefinitions]);

  const isDirty: boolean = JSON.stringify(definitions) !== savedSnapshot;
  const boundCount: number = countBoundDocumentParameters(definitions);

  const resetDraft = (): void => {
    setDraftTagName("");
    setDraftSymbol(undefined);
    setPickerGeneration((generation: number) => generation + 1);
  };

  /** Why the last row cannot become a parameter yet, or `null` when it can. */
  const draftError = (tagName: string, symbol: ISymbol | undefined): string | null => {
    if (!isValidTagName(tagName)) {
      return `"${tagName || "(empty)"}" is not a valid parameter name. Use uppercase letters, digits and underscore only (2–64 characters), without the angle brackets.`;
    }

    const duplicate: boolean = definitions.some(
      (definition: IDocumentParameterDefinition) => (definition.tagName ?? "").trim() === tagName
    );
    if (duplicate) {
      return `Duplicate parameter: ${tagName}`;
    }

    // One check, because there is one control: the symbol carries both the source and the field.
    if (!isDocumentParameterBound(symbol)) {
      return `Pick a value for <${tagName}> — a source action's field, or a document input.`;
    }

    return null;
  };

  /**
   * The last row becomes a parameter as soon as it has both halves — the same two-field rule the table editor's
   * column grid and the session variables list use. There is no add step: what the list shows is what will be saved.
   *
   * Called from the two moments that can complete it, and from neither before: picking a value, which is a single
   * deliberate choice, and leaving the name, which is the only way a typed name can say it is finished.
   */
  const commitDraft = (tagName: string, symbol: ISymbol | undefined): void => {
    if (!tagName.trim() || !isDocumentParameterBound(symbol)) return;

    const error: string | null = draftError(tagName, symbol);
    if (error) {
      KMainFunctions.displayWarningNotification(error);
      return;
    }

    setDefinitions((current: IDocumentParameterDefinition[]) => [...current, { tagName: tagName.trim(), symbol }]);
    resetDraft();
  };

  /** Saved rows are corrected where they are shown; nothing is loaded back into a form to be edited. */
  const updateDefinition = (row: IDocumentParameterDefinition, patch: Partial<IDocumentParameterDefinition>): void => {
    setDefinitions((current: IDocumentParameterDefinition[]) =>
      current.map((definition: IDocumentParameterDefinition) =>
        definition === row ? { ...definition, ...patch } : definition
      )
    );
  };

  /** A rename is refused rather than applied when it breaks the tag rules or collides with another row. */
  const renameDefinition = (row: IDocumentParameterDefinition, tagName: string): boolean => {
    const trimmed: string = tagName.trim();
    if (!isValidTagName(trimmed)) {
      KMainFunctions.displayWarningNotification(
        `"${trimmed || "(empty)"}" is not a valid parameter name. Use uppercase letters, digits and underscore only (2–64 characters), without the angle brackets.`
      );
      return false;
    }
    const duplicate: boolean = definitions.some(
      (definition: IDocumentParameterDefinition) => definition !== row && (definition.tagName ?? "").trim() === trimmed
    );
    if (duplicate) {
      KMainFunctions.displayWarningNotification(`Duplicate parameter: ${trimmed}`);
      return false;
    }
    updateDefinition(row, { tagName: trimmed });
    return true;
  };

  const removeDefinition = (definition: IDocumentParameterDefinition): void => {
    const index: number = definitions.indexOf(definition);
    if (index === -1) return;

    setDefinitions((current: IDocumentParameterDefinition[]) =>
      current.filter((_definition: IDocumentParameterDefinition, position: number) => position !== index)
    );
  };

  /**
   * The same rules again over the whole list, because the list can also arrive from the server — a definition
   * stored before a tag rule changed must not be saved back silently.
   */
  const validationError = (): string | null => {
    const seen: Set<string> = new Set<string>();

    for (const definition of definitions) {
      const tagName: string = (definition.tagName ?? "").trim();
      if (!isValidTagName(tagName)) {
        return `"${tagName || "(empty)"}" is not a valid parameter name. Use uppercase letters, digits and underscore only (2–64 characters), without the angle brackets.`;
      }
      if (seen.has(tagName)) {
        return `Duplicate parameter: ${tagName}`;
      }
      seen.add(tagName);

      if (!isDocumentParameterBound(definition.symbol as ISymbol | undefined)) {
        return `Pick a value for <${tagName}> — a source action's field, or a document input.`;
      }
    }
    return null;
  };

  /**
   * Back to the last saved list, and the row being written cleared with it — an abandoned half of a parameter must
   * not be left in the last row, where the next value picked would take it for a finished one.
   */
  const discard = (): void => {
    setDefinitions(JSON.parse(savedSnapshot) as IDocumentParameterDefinition[]);
    resetDraft();
  };

  /**
   * Out of the tool, to the tree the Edit was clicked in. This is its own `DashboardState`, so leaving is the same
   * call that entered it (`data-diagram-scheme-window`'s `openDocumentParameters`) with the states swapped —
   * Session Variables, one node above in the same tree, returns to the same place.
   */
  const leave = (): void => {
    sd?.setDashboardState(DashboardState.dataDiagramScheme);
  };

  /**
   * What the back control runs. `KuikaEditorHeader`'s back is the only way out of an editor, so it has to be the
   * guarded close rather than a bare discard: with a dirty table the prompt asks first, and Discard is where
   * abandoning the edits lives now.
   */
  const requestClose = (): void => {
    if (!isDirty) {
      leave();
      return;
    }
    setIsClosePromptOpen(true);
  };

  /** @param afterSave Runs once the ✓ has been held, so "Save & close" never leaves before its confirmation. */
  const save = (afterSave?: () => void): void => {
    // Not while a save runs, and not during its check: a second press there would save again behind it.
    if (!saveMachine.canStart) {
      return;
    }

    const error: string | null = validationError();
    if (error) {
      KMainFunctions.displayErrorNotification(error);
      return;
    }

    saveMachine.start();
    // Nothing to reconcile on the way out: the symbol is the source of truth, and the backend derives the action
    // and field from it on every read.
    const payload: IDocumentParameterDefinition[] = definitions.map((definition: IDocumentParameterDefinition) => ({
      ...definition,
      tagName: (definition.tagName ?? "").trim()
    }));

    // Through the provider: it owns every part of the section, so saving here directly would drop whatever the
    // right panel changed in the meantime. The list is handed over explicitly — the provider must not have to read
    // it out of state that this render has not committed yet.
    sd.saveDocumentParameterDefinitions(payload, inputs)
      .then(() => {
        setDefinitions(payload);
        setSavedSnapshot(JSON.stringify(payload));
        // The section stays open on the list it just saved, so SAVE's own check is the confirmation.
        saveMachine.succeed(afterSave);
      })
      .catch((saveError: unknown) => {
        saveMachine.fail();
        KMainFunctions.exceptionHandler(saveError);
      });
  };

  /**
   * The empty state carries the reference detail that used to sit permanently above the table. Here it is read once,
   * by someone who needs it; there it prefaced every visit to a surface people open repeatedly.
   */
  const renderEmptyState = () => (
    <div className="kuika-document-parameters__empty" data-testid="documentParametersEmpty">
      {hasSources ? (
        <>
          <KuikaSmallMediumText>No parameters yet</KuikaSmallMediumText>
          <KuikaSmallText>
            Name one above, then point it at a source action&apos;s returned field or a document input.
          </KuikaSmallText>
          <KuikaSmallText>
            Tag names are uppercase and matched exactly — <code>&lt;SIRKET_ADI&gt;</code> and{" "}
            <code>&lt;SIRKET_ADİ&gt;</code> are different parameters.
          </KuikaSmallText>
        </>
      ) : (
        <>
          <KuikaSmallMediumText>Nothing to build a value from yet</KuikaSmallMediumText>
          <KuikaSmallText>
            In the right panel, declare a document input or add a source action. A parameter&apos;s value is one of
            those actions&apos; returned fields, or an input on its own.
          </KuikaSmallText>
        </>
      )}
    </div>
  );

  /**
   * Row controls follow the saved list, not the strip: while a row is being edited they are hidden rather than
   * removed, so the list does not reflow under the pointer mid-edit. Same reasoning — and the same two glyphs — as
   * the Session Variables list.
   */
  /** The wand is the picker's only trigger; the hint sits over the dead part of the cell and hands the click on.
   *  The Cascader's trigger, or the columns popup's button when `symbolPickerColumnsV2` is on. */
  const openValuePicker = (hint: HTMLElement): void => {
    const trigger: HTMLElement | null | undefined = hint
      .closest(".kuika-document-parameters__value-cell")
      ?.querySelector<HTMLElement>(".ant-cascader-picker, .kuika-symbol-picker-trigger__button");
    trigger?.click();
  };

  const columns = (): KuikaTableColumn<IDocumentParameterRow>[] => [
    {
      id: "tagName",
      header: () => "Parameter",
      accessorFn: (row: IDocumentParameterRow) => row.tagName ?? "",
      // The angle brackets are syntax, not value: fixed affordances around the field rather than typed. That also
      // stops the tag being saved with brackets included, which is the single easiest thing to get wrong here.
      cell: (row: IDocumentParameterRow) => (
        <span className="kuika-document-parameters__tag-cell">
          <span className="kuika-document-parameters__bracket" aria-hidden="true">
            &lt;
          </span>
          {row.isDraft ? (
            <KuikaInput
              borderless
              value={draftTagName}
              placeholder="SIRKET_ADI"
              data-testid="documentParameterTagName"
              // Opening the tool and then having to click into the row is the kind of friction that makes a
              // surface feel unfinished.
              autoFocus
              onChange={(_event: unknown, next: string | number): void => setDraftTagName(String(next ?? ""))}
              onBlur={(): void => commitDraft(draftTagName, draftSymbol)}
              onPressEnter={(): void => commitDraft(draftTagName, draftSymbol)}
            />
          ) : (
            <TagNameCell value={row.tagName ?? ""} onCommit={(next: string): boolean => renameDefinition(row, next)} />
          )}
          <span className="kuika-document-parameters__bracket" aria-hidden="true">
            &gt;
          </span>
        </span>
      )
    },
    {
      id: "value",
      header: () => "Value",
      accessorFn: (row: IDocumentParameterRow) => describeValue(row.symbol as ISymbol | undefined, actions),
      cell: (row: IDocumentParameterRow) => (
        <span className="kuika-document-parameters__value-cell">
          <SymbolPicker
            // Position for the same reason the row is keyed by it: the tag name is being edited, and keying the
            // picker by it remounted the picker under the caret on every accepted rename.
            key={
              row.isDraft
                ? `documentParameterValue-${pickerGeneration}`
                : `documentParameterValue-${definitions.indexOf(row as IDocumentParameterDefinition)}`
            }
            data_testid={row.isDraft ? "documentParameterValue" : `documentParameterValue-${row.tagName}`}
            symbol={(row.isDraft ? draftSymbol : (row.symbol as ISymbol | undefined)) as ISymbol}
            changeType="onBlur"
            hideFixedValue
            showInSingleLine
            stablePopup
            // Right-aligned to the trigger, so the box grows leftwards and the columns already open stay put.
            // The default left anchor made every new column push the visible ones sideways.
            popupPlacement="bottomRight"
            onSelectedSymbol={(picked: ISymbol): void => {
              if (!row.isDraft) {
                updateDefinition(row, { symbol: picked });
                return;
              }
              setDraftSymbol(picked);
              commitDraft(draftTagName, picked);
            }}
          />
          {/* Says what is missing instead of colouring the row — a marker with no legend makes the reader guess.
            It also has to OPEN the picker. The single-line picker is `[children][20px wand]` and this cell passes
            no children, so the wand was the only live pixel in a 360px cell that reads as a field: measured on the
            running designer, a click anywhere left of the wand did nothing at all. The hint is what the reader
            aims at, so the hint is what hands the click on. */}
          {!isDocumentParameterBound((row.isDraft ? draftSymbol : row.symbol) as ISymbol | undefined) && (
            <span
              className="kuika-document-parameters__needs-value"
              role="button"
              tabIndex={0}
              onClick={(event: MouseEvent<HTMLSpanElement>): void => openValuePicker(event.currentTarget)}
              onKeyDown={(event: KeyboardEvent<HTMLSpanElement>): void => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                openValuePicker(event.currentTarget);
              }}
            >
              Pick a source action field or an input
            </span>
          )}
        </span>
      )
    },
    {
      id: "actions",
      header: () => "",
      width: 70,
      align: "center",
      // The empty row at the end is not a parameter yet, so there is nothing there to remove.
      cell: (row: IDocumentParameterRow) =>
        row.isDraft ? null : (
          <div className="kuika-document-parameters__row-actions">
            <Trash12RedSvg
              data-testid={`documentParameterRemove-${row.tagName}`}
              onClick={() => removeDefinition(row)}
            />
          </div>
        )
    }
  ];

  /** What stands under the intro: the list in outline, nothing yet, the empty state, or the list itself. */
  const renderList = (): React.ReactNode => {
    if (presentation === "pending") {
      return null;
    }

    if (presentation === "skeleton") {
      return (
        <KuikaSkeletonRegion label="Loading document parameters">
          <KuikaSkeletonList rows={4} withIcon={false} />
        </KuikaSkeletonRegion>
      );
    }

    // Nothing to point a parameter at yet, so there is nothing to write: the row to write in only appears once the
    // right panel has a document input or a source action to build a value from. Offering the row first would ask
    // for a name whose value could not then be picked.
    if (!hasSources) {
      return renderEmptyState();
    }

    return (
      <>
        <KuikaTable<IDocumentParameterRow>
          data-testid="documentParametersTable"
          // The saved rows key by name, which only changes when a rename is committed; the trailing row keeps one
          // key of its own so typing in it never re-keys the row under the caret.
          // Keyed by position, never by the tag name. The name is the field being edited, so keying by it
          // re-keyed the row the moment a rename was accepted: React unmounted the whole row subtree and
          // the caret landed on <body>. Position survives a rename; the list is appended to and never
          // reordered, so the only rows that re-key are the ones after a delete, where nothing is focused.
          rowKey={(row: IDocumentParameterRow) =>
            row.isDraft ? "__draft__" : `row-${definitions.indexOf(row as IDocumentParameterDefinition)}`
          }
          columns={columns()}
          // The parameter being written is the last row of the list, not a form above it.
          data={[...definitions, { tagName: "", symbol: draftSymbol, isDraft: true }]}
          // The list is as long as the project's parameters and no longer; a page control over a handful of
          // rows would be noise.
          enablePagination={false}
          // The frame ends where its content does, rather than on the kit's 400px floor — this surface has no
          // filter, so a short list would otherwise hang a bordered strip under the last row.
          fitContentHeight
        />

        {/* Kept under the list rather than in place of it: the row to write in is on screen, and this is the
          reference detail that goes with an empty one. */}
        {definitions.length === 0 && renderEmptyState()}
      </>
    );
  };

  return (
    <div className="kuika-document-parameters">
      {/* Same bar the other tools on this tree carry, drawn after Session Variables, which does the same kind of
            work one node above: flush with the column, the glyph-and-name identity, the back arrow. Back leaves the
            tool, as its label says and as Session Variables' does — it used to only roll the table back to its last
            saved list, so a bar that reads "Back to Datasources" went nowhere. Abandoning edits now lives in the
            prompt that back raises. */}
      <div className="kuika-document-parameters__header">
        <KuikaEditorHeader
          title="Document Parameters"
          backLabel="Back to Datasources"
          // The project has exactly one of these, so the name is an identity with nothing to switch to.
          renderTitle={(title: string): JSX.Element => (
            <KuikaEditorIdentity
              icon={<EditorGlyphIcon glyph={DOCUMENT_PARAMETERS_GLYPH} />}
              iconTooltip={DOCUMENT_PARAMETERS_GLYPH.label}
              name={title}
              nameTestId="documentParametersTitle"
            />
          )}
          submitLabel="SAVE"
          onSubmit={(): void => save()}
          onCancel={requestClose}
          status={saveStatus}
          // Clean and idle is the only off state: the check stays lit on the list it just saved.
          isSubmitDisabled={!isDirty && saveStatus === "idle"}
          cancelTestId="documentParameterDiscard"
          submitTestId="documentParameterSave"
          renderSubmit={(submitButton: React.ReactNode): React.ReactNode => (
            <>
              <span className="kuika-document-parameters__status">
                {/* The count carries the state that matters at a glance: how many parameters can actually fill. */}
                <KuikaSmallText>
                  {definitions.length === 0
                    ? ""
                    : isDirty
                      ? "Unsaved changes"
                      : `${boundCount} of ${definitions.length} ready`}
                </KuikaSmallText>
              </span>
              {submitButton}
            </>
          )}
        />
      </div>

      <div className="kuika-document-parameters__body">
        <div className="kuika-document-parameters__intro">
          <KuikaSmallText>
            Each <code>&lt;TAG&gt;</code> left in a DMS template or document, and where its value comes from. Filled
            when the document is viewed or downloaded.
          </KuikaSmallText>
        </div>

        <div className="kuika-document-parameters__table">{renderList()}</div>
      </div>

      {/* The same prompt every editor on this tree raises on a dirty close, so Discard and Save & close read the
          same here as they do in Session Variables. */}
      <CloseConfirmationModal
        itemName="Document Parameters"
        closeType="Document Parameters"
        triggerModal={isClosePromptOpen}
        isLoading={saveStatus === "loading"}
        updateAndCloseHandler={(): void => {
          save((): void => {
            setIsClosePromptOpen(false);
            leave();
          });
        }}
        closeWithoutSaveHandler={(): void => {
          setIsClosePromptOpen(false);
          discard();
          leave();
        }}
        cancel={(): void => setIsClosePromptOpen(false)}
      />
    </div>
  );
};

const documentParametersSection = withSD<IDocumentParametersSectionProps>(DocumentParametersSectionInner);
export { documentParametersSection as DocumentParametersSection };
